import { useEffect, useMemo, useRef, useState } from 'react';
import { ALL_CHESS960_POSITIONS, searchChess960Positions } from '../utils/chess960Utils';

interface Chess960PositionSelectorProps {
  /** Currently selected position ID (null = random) */
  value: number | null;
  onChange: (id: number | null) => void;
}

const VISIBLE_LIMIT = 40;

/**
 * Compact searchable dropdown for all 960 Fischer Random starting positions.
 * The user can search by:
 *   - position number (#0–#959)
 *   - piece arrangement string (e.g. "RNBQKBNR")
 * When null is selected, the game uses a random position each time.
 */
export const Chess960PositionSelector = ({ value, onChange }: Chess960PositionSelectorProps) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter positions based on search query
  const filtered = useMemo(() => {
    return searchChess960Positions(query, VISIBLE_LIMIT);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selectedPos = value !== null ? ALL_CHESS960_POSITIONS.find((position) => position.id === value) ?? null : null;

  const handleSelect = (id: number | null) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  const handleInputFocus = () => {
    setOpen(true);
    setQuery('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }

    if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      handleSelect(filtered[0].id);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger / search input */}
      <div
        className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 transition-colors cursor-text ${
          open
            ? 'border-cyan-400/70 bg-slate-800/80 shadow-[0_0_0_2px_rgba(6,182,212,0.2)]'
            : 'border-slate-600/70 bg-slate-900/60 hover:border-slate-500'
        }`}
        onClick={() => { inputRef.current?.focus(); setOpen(true); }}
      >
        {/* Chess knight icon */}
        <span className="text-lg leading-none select-none text-cyan-300 flex-shrink-0" aria-hidden>♞</span>

        <input
          ref={inputRef}
          type="text"
          className="flex-1 bg-transparent text-sm text-white placeholder-slate-400 outline-none min-w-0"
          placeholder={selectedPos ? 'Search by # or piece layout' : 'Random position — search by # or pieces'}
          value={open ? query : (selectedPos?.display ?? '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          aria-label="Search Chess960 position"
          aria-expanded={open}
          aria-haspopup="listbox"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
        />

        {/* Current selection badge */}
        {!open && selectedPos && (
          <span className="flex-shrink-0 rounded-md bg-cyan-500/20 px-2 py-0.5 font-mono text-xs text-cyan-200 border border-cyan-500/30">
            #{selectedPos.id}
          </span>
        )}

        {/* Clear button */}
        {value !== null && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleSelect(null); }}
            className="flex-shrink-0 rounded p-0.5 text-slate-400 hover:text-white transition-colors"
            aria-label="Clear selection (use random position)"
            title="Use random position"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <svg
          className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown list */}
      {open && (
        <div className="mt-1.5 w-full rounded-xl border border-slate-600/70 bg-slate-900 shadow-2xl shadow-black/60 overflow-hidden">
          {/* Random option at top */}
          <div className="border-b border-slate-700/60">
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors text-left ${
                value === null
                    ? 'bg-cyan-500/15 text-cyan-200'
                    : 'text-slate-300 hover:bg-slate-800/70'
              }`}
            >
              <span className="text-base leading-none text-slate-400" aria-hidden>🎲</span>
              <div>
                <div className="font-medium">Random position</div>
                <div className="text-xs text-slate-500">A different Fischer Random position each game</div>
              </div>
              {value === null && (
                <svg className="ml-auto w-4 h-4 text-cyan-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          </div>

          {/* Position list */}
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Chess960 positions"
            className="max-h-64 overflow-y-auto overscroll-contain"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-slate-500">No positions match your search</li>
            ) : (
              filtered.map((pos) => {
                const isSelected = value === pos.id;
                return (
                  <li key={pos.id} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => handleSelect(pos.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors text-left ${
                        isSelected
                          ? 'bg-cyan-500/15 text-cyan-200'
                          : 'text-slate-300 hover:bg-slate-800/70'
                      }`}
                    >
                      {/* Position number badge */}
                      <span className="flex-shrink-0 w-12 text-right font-mono text-xs text-slate-500">
                        #{pos.id}
                      </span>
                      {/* Piece arrangement with coloured squares */}
                      <span className="flex-1 font-mono tracking-widest text-xs text-slate-100 truncate">
                        {pos.id === 518 ? (
                          <>{pos.label}<span className="ml-1.5 font-sans font-normal text-cyan-400 no-underline tracking-normal">(Standard)</span></>
                        ) : pos.label}
                      </span>
                      {isSelected && (
                        <svg className="ml-auto w-4 h-4 text-cyan-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          {/* Footer hint */}
          <div className="border-t border-slate-700/60 px-3 py-2 text-center">
            <p className="text-xs text-slate-500">
              {filtered.length < ALL_CHESS960_POSITIONS.length
                ? `Showing ${filtered.length} matches — type pieces left to right (e.g. NN for two knights), or search by number`
                : `Showing first ${VISIBLE_LIMIT} of 960 — type a starting piece order (e.g. NN, RNB) or a position number`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
